{#if $isUserLoggedIn}

<div class="community-settings-section">
  <FocusRestoration realm="settings-community">
    <RadioGroup
      id="pinnables"
      length={numPinnable}
      label="{$messages["pinnableTimelines"]}">

      <h2 class="community-header community-header-first">
        {$messages["timelines"]}
      </h2>

      <PageList label="{$messages["timelines"]}">
        {#each staticPinnables as staticPinnable, i}
          <PageListItem
            href={staticPinnable.href}
            label={staticPinnable.label}
            icon={staticPinnable.icon}
            pinnable="true"
            pinIndex={i}
          />
        {/each}
      </PageList>

      {#if $listsSupported !== false}
      <h2 class="community-header">
        {$messages["lists"]}
      </h2>

      <PageList label="{$messages["lists"]}">
        {#each sortedLists as list, i}
          <PageListItem
            href="/lists/{list.id}"
            label={list.title}
            icon="#fa-bars"
            pinnable="true"
            pinIndex={staticPinnablesLength + i}
          />
        {/each}
        <PageListActionItem
          label="{$messages["createList"]}"
          icon="#fa-plus"
          on:click="onCreateListClicked()"
        />
      </PageList>
      {/if}

    </RadioGroup>

    <div class="instance-settings-section">
      <h2 class="community-header">
        {$messages["instanceSettings"]}
      </h2>

      <PageList label="{$messages["instanceSettings"]}">
        {#if isLockedAccount}
          <PageListItem
            href="/requests"
            label={followRequestsLabel}
            icon="#fa-user-plus"
          />
        {/if}

        <PageListItem
          href="/muted"
          label="{$messages["mutedUsers"]}"
          icon="#fa-volume-off"
        />

        <PageListItem
          href="/blocked"
          label="{$messages["blockedUsers"]}"
          icon="#fa-ban"
        />

        <PageListItem
          href="/pinned"
          label="{$messages["pinnedStatuses"]}"
          icon="#fa-thumb-tack"
        />

        <PageListItem
          href="/scheduled"
          label="{$messages["scheduledPosts"]}"
          icon="#fa-clock"
        />

        {#if $listsSupported !== false && listsLength > 0}
          <PageListItem
            href="/manage-lists"
            label="{$messages["manageLists"]}"
            icon="#fa-bars"
          />
        {/if}

        <PageListItem
          href="/followed-tags"
          label="{$messages["followedHashtags"]}"
          icon="#fa-hashtag"
        />
      </PageList>
    </div>

  </FocusRestoration>
</div>

{/if}

<style>
  .community-settings-section {
    margin-top: 32px;
  }

  .community-header {
    font-size: 23px;
    font-weight: 400;
    line-height: 1.3;
    margin: 32px 0 18px;
  }

  .community-header-first {
    margin-top: 0;
  }

  .instance-settings-section {
    margin-top: 32px;
  }

  :global([href="/followed-tags"] .page-list-item-svg) {
    width: 20px;
    height: 20px;
  }
</style>

<script>
  import { store } from '../../_store/store.js'
  import PageList from '../community/PageList.html'
  import PageListItem from '../community/PageListItem.html'
  import PageListActionItem from '../community/PageListActionItem.html'
  import RadioGroup from '../radio/RadioGroup.html'
  import FocusRestoration from '../FocusRestoration.html'
  import { updateListsForInstance } from '../../_actions/lists.js'
  import { updateFollowRequestCountIfLockedAccount } from '../../_actions/followRequests.js'
  import { formatIntl } from '../../_utils/formatIntl.js'
  import { importShowCreateListDialog } from '../dialog/asyncDialogs/importShowCreateListDialog.js'

  export default {
    async oncreate () {
      const { currentInstance } = this.store.get()

      if (currentInstance) {
        // Best-effort refresh: a failure (rate-limit, 5xx, network blip, or a backend
        // without lists support) must not surface as an uncaught promise rejection. The
        // lists UI falls back to whatever is cached in the store.
        await Promise.all([
          updateListsForInstance(currentInstance).catch(e => console.warn('failed to update lists', e)),
          updateFollowRequestCountIfLockedAccount(currentInstance).catch(e => console.warn('failed to update follow request count', e))
        ])
      }
    },

    store: () => store,

    components: {
      PageList,
      PageListItem,
      PageListActionItem,
      RadioGroup,
      FocusRestoration
    },

    methods: {
      async onCreateListClicked () {
        const showCreateListDialog = await importShowCreateListDialog()
        await showCreateListDialog()
      }
    },

    computed: {
      isLockedAccount: ({ $currentVerifyCredentials }) =>
        $currentVerifyCredentials &&
        $currentVerifyCredentials.locked,

      followRequestsLabel: ({
        $hasFollowRequests,
        $numberOfFollowRequests
      }) => (
        formatIntl('intl.followRequestsLabel', {
          hasFollowRequests: $hasFollowRequests,
          count: $numberOfFollowRequests
        })
      ),

      listsLength: ({ $lists }) =>
        $lists ? $lists.length : 0,

      // Sort lists alphabetically by title so the settings menu matches the Manage-lists page
      // (which sorts the same way). There is no server-side list order to honour, so a stable
      // alphabetical order is the predictable choice across both views.
      sortedLists: ({ $lists }) =>
        ($lists || []).slice().sort((a, b) => a.title.localeCompare(b.title)),

      staticPinnablesLength: ({ staticPinnables }) =>
        staticPinnables.length,

      numPinnable: ({
        listsLength,
        staticPinnablesLength
      }) => (
        listsLength + staticPinnablesLength
      ),

      staticPinnables: ({ $bubbleTimelineEnabled, $messages }) => [
        {
          href: '/local',
          label: $messages.localTimeline,
          icon: '#fa-users'
        },
        ...($bubbleTimelineEnabled
          ? [{
              href: '/bubble',
              label: $messages.bubbleTimeline,
              icon: '#fa-circle'
            }]
          : []),
        {
          href: '/federated',
          label: $messages.federatedTimeline,
          icon: '#fa-globe'
        },
        {
          href: '/favorites',
          label: $messages.favorites,
          icon: '#fa-star'
        },
        {
          href: '/direct',
          label: $messages.directMessages,
          icon: '#fa-envelope'
        },
        {
          href: '/bookmarks',
          label: $messages.bookmarks,
          icon: '#fa-bookmark'
        }
      ]
    }
  }
</script>
